import React, { useState, useEffect, useMemo } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Progress } from '../ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { toast } from 'sonner';
import { Repeat, Plus, Trash2, Pencil, Clock, BarChart3, CalendarDays, CheckCircle2, AlertTriangle, XCircle, Activity, Grid3x3, Settings, FileBarChart, TrendingUp, Users } from 'lucide-react';
import PageHeader from '../ui-pro/PageHeader';
import StatCard from '../ui-pro/StatCard';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const DAYS = [
  { v: 0, l: 'Pzt' }, { v: 1, l: 'Sal' }, { v: 2, l: 'Çar' }, { v: 3, l: 'Per' },
  { v: 4, l: 'Cum' }, { v: 5, l: 'Cmt' }, { v: 6, l: 'Paz' },
];
const monthStart = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`; };
const todayISO = () => new Date().toISOString().split('T')[0];
const dm = (ds) => { try { const d = new Date(ds); return `${d.getDate()}/${d.getMonth() + 1}`; } catch { return ds; } };
const emptyForm = { id: null, title: '', days_of_week: [0, 1, 2, 3, 4], latest_time: '' };

/* ============ Reusable: bir personelin rutin tanımları (personel detayında da kullanılır) ============ */
export const RoutineDefinitions = ({ personnelId, personnelName }) => {
  const [routines, setRoutines] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const fetchRoutines = async () => {
    if (!personnelId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/personnel-routines`, { params: { personnel_id: personnelId } });
      setRoutines(res.data?.items || []);
    } catch { toast.error('Rutinler yüklenemedi'); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchRoutines(); /* eslint-disable-next-line */ }, [personnelId]);

  const toggleDay = (v) => setForm(f => ({ ...f, days_of_week: f.days_of_week.includes(v) ? f.days_of_week.filter(x => x !== v) : [...f.days_of_week, v].sort((a, b) => a - b) }));
  const openCreate = () => { setForm(emptyForm); setShowForm(true); };
  const openEdit = (r) => { setForm({ id: r.id, title: r.title, days_of_week: r.days_of_week || [], latest_time: r.latest_time || '' }); setShowForm(true); };

  const saveForm = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error('Rutin başlığı zorunlu'); return; }
    if (form.days_of_week.length === 0) { toast.error('En az bir gün seçin'); return; }
    try {
      if (form.id) {
        await axios.put(`${API}/personnel-routines/${form.id}`, { title: form.title.trim(), days_of_week: form.days_of_week, latest_time: form.latest_time || null });
        toast.success('Rutin güncellendi');
      } else {
        await axios.post(`${API}/personnel-routines`, { personnel_id: personnelId, title: form.title.trim(), days_of_week: form.days_of_week, latest_time: form.latest_time || null });
        toast.success('Rutin eklendi');
      }
      setShowForm(false); fetchRoutines();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Kaydedilemedi'); }
  };
  const toggleActive = async (r) => { try { await axios.put(`${API}/personnel-routines/${r.id}`, { is_active: !r.is_active }); fetchRoutines(); } catch { toast.error('Güncellenemedi'); } };
  const del = async (r) => { if (!window.confirm(`"${r.title}" rutini silinsin mi?\n\nGeçmiş kayıtları da silinir.`)) return; try { await axios.delete(`${API}/personnel-routines/${r.id}`); toast.success('Rutin silindi'); fetchRoutines(); } catch { toast.error('Silinemedi'); } };

  return (
    <div className="space-y-3" data-testid="routine-definitions">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold text-slate-700">{personnelName ? `${personnelName} — Rutinler` : 'Rutin Tanımları'} <span className="text-slate-400 font-normal">({routines.length})</span></p>
        <Button onClick={openCreate} size="sm" disabled={!personnelId} className="bg-teal-600 hover:bg-teal-700" data-testid="routines-add-btn"><Plus className="w-4 h-4 mr-1" /> Rutin Ekle</Button>
      </div>
      <Card><CardContent className="p-0">
        {loading ? <p className="text-center py-8 text-slate-400 text-sm">Yükleniyor...</p>
          : routines.length === 0 ? <p className="text-center py-8 text-slate-400 text-sm">Henüz rutin tanımlanmadı</p>
          : (
            <div className="divide-y" data-testid="routines-list">
              {routines.map(r => (
                <div key={r.id} className={`flex items-center gap-3 p-3 ${!r.is_active ? 'opacity-50' : ''}`} data-testid={`routine-item-${r.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-medium text-sm text-slate-800">{r.title}</p>
                      {r.latest_time && <Badge className="bg-amber-100 text-amber-700 border-0 text-[10px]"><Clock className="w-2.5 h-2.5 mr-0.5" /> en geç {r.latest_time}</Badge>}
                      {!r.is_active && <Badge className="bg-slate-200 text-slate-600 border-0 text-[10px]">Pasif</Badge>}
                    </div>
                    <div className="flex items-center gap-1 mt-1.5 flex-wrap">
                      {DAYS.map(d => <span key={d.v} className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${(r.days_of_week || []).includes(d.v) ? 'bg-teal-100 text-teal-700' : 'bg-slate-100 text-slate-300'}`}>{d.l}</span>)}
                    </div>
                  </div>
                  <Switch checked={!!r.is_active} onCheckedChange={() => toggleActive(r)} data-testid={`routine-active-${r.id}`} />
                  <Button size="sm" variant="ghost" onClick={() => openEdit(r)} className="h-7 w-7 p-0" data-testid={`routine-edit-${r.id}`}><Pencil className="w-3.5 h-3.5 text-slate-500" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => del(r)} className="h-7 w-7 p-0" data-testid={`routine-delete-${r.id}`}><Trash2 className="w-3.5 h-3.5 text-rose-500" /></Button>
                </div>
              ))}
            </div>
          )}
      </CardContent></Card>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg" data-testid="routine-form-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Repeat className="w-5 h-5 text-teal-600" /> {form.id ? 'Rutini Düzenle' : 'Yeni Rutin'}</DialogTitle>
            <DialogDescription>{personnelName} için günlük rutin işi</DialogDescription>
          </DialogHeader>
          <form onSubmit={saveForm} className="space-y-4">
            <div><Label>Rutin Başlığı *</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Örn: Salonu temizle ve havalandır" data-testid="routine-title-input" /></div>
            <div>
              <Label className="mb-1.5 block">Hangi Günler? *</Label>
              <div className="flex flex-wrap gap-1.5" data-testid="routine-days">
                {DAYS.map(d => { const on = form.days_of_week.includes(d.v); return (
                  <button key={d.v} type="button" onClick={() => toggleDay(d.v)} className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-all ${on ? 'bg-teal-600 text-white border-teal-600' : 'bg-white text-slate-600 border-slate-200 hover:border-teal-300'}`} data-testid={`routine-day-${d.v}`}>{d.l}</button>
                ); })}
              </div>
            </div>
            <div>
              <Label>En Geç Saat (opsiyonel)</Label>
              <Input type="time" value={form.latest_time} onChange={(e) => setForm({ ...form, latest_time: e.target.value })} className="w-40" data-testid="routine-time-input" />
              <p className="text-[11px] text-slate-400 mt-1">Bu saatten sonra işaretlenirse rapora "geç" olarak yansır.</p>
            </div>
            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowForm(false)} className="flex-1">İptal</Button>
              <Button type="submit" className="flex-1 bg-teal-600 hover:bg-teal-700" data-testid="routine-save-btn">{form.id ? 'Güncelle' : 'Ekle'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

/* ============ KPI kartı ============ */
const KpiCard = ({ icon: Icon, title, value, sub, color, testid }) => {
  const colors = {
    emerald: 'text-emerald-600 bg-emerald-50', sky: 'text-sky-600 bg-sky-50',
    indigo: 'text-indigo-600 bg-indigo-50', rose: 'text-rose-600 bg-rose-50',
  }[color] || 'text-slate-600 bg-slate-50';
  return (
    <Card className="border border-slate-200/90 shadow-sm" data-testid={testid}><CardContent className="p-4">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{title}</p>
          <p className="text-2xl font-black tracking-tight text-slate-900 mt-1">{value}</p>
          {sub && <p className="text-[11px] text-slate-400 mt-0.5">{sub}</p>}
        </div>
        <div className={`p-2 rounded-lg ${colors}`}><Icon className="w-5 h-5" /></div>
      </div>
    </CardContent></Card>
  );
};

/* ============ Ana sayfa ============ */
const VIEWS = [
  { id: 'live', label: 'Bugün Canlı Durum', icon: Activity },
  { id: 'weekly', label: 'Haftalık Izgara', icon: Grid3x3 },
  { id: 'defs', label: 'Rutin Tanımları', icon: Settings },
  { id: 'report', label: 'Rapor', icon: FileBarChart },
];

const RoutinesTab = ({ personnelList = [] }) => {
  const [view, setView] = useState('live');
  const activePersonnel = useMemo(() => personnelList.filter(p => p.active !== false), [personnelList]);
  const [ov, setOv] = useState(null);
  const [ovLoading, setOvLoading] = useState(false);
  const [selectedPid, setSelectedPid] = useState('');

  useEffect(() => { if (!selectedPid && activePersonnel.length > 0) setSelectedPid(activePersonnel[0].id); }, [activePersonnel, selectedPid]);

  const fetchOverview = async () => {
    setOvLoading(true);
    try { const res = await axios.get(`${API}/routines/overview`); setOv(res.data); }
    catch { /* sessiz */ }
    finally { setOvLoading(false); }
  };
  useEffect(() => { if (view === 'live' || view === 'weekly') fetchOverview(); /* eslint-disable-next-line */ }, [view]);

  const kpi = ov?.kpi || {};

  return (
    <div className="space-y-4" data-testid="routines-tab-root">
      <PageHeader
        icon={Repeat}
        title="Rutin İşler ve Personel Takibi"
        description="Günlük rutinleri tanımla, canlı takip et ve zamanında tamamlama performansını analiz et"
        testid="routines-page-header"
      >
        <div className="flex flex-wrap items-center gap-1.5">
          {VIEWS.map(v => {
            const Icon = v.icon;
            return (
              <button key={v.id} onClick={() => setView(v.id)}
                className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-[13px] font-medium transition-colors ${view === v.id ? 'bg-[#0B172A] text-white' : 'text-slate-600 hover:bg-slate-100'}`}
                data-testid={`routines-view-${v.id}`}>
                <Icon className="w-4 h-4" /> {v.label}
              </button>
            );
          })}
        </div>
      </PageHeader>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard icon={CheckCircle2} tone="emerald" testid="kpi-today-progress" label="Bugün Tamamlanan" value={`${kpi.completed_today ?? 0}/${kpi.total_today ?? 0}`} sub="rutin işlem" />
        <StatCard icon={Clock} tone="sky" testid="kpi-pending-today" label="Bugün Bekleyen" value={kpi.pending_today ?? 0} sub="henüz yapılmadı" />
        <StatCard icon={TrendingUp} tone="indigo" testid="kpi-weekly-ontime" label="Haftalık Zamanında" value={`%${kpi.weekly_ontime_rate ?? 0}`} sub="bu hafta" />
        <StatCard icon={AlertTriangle} tone="rose" testid="kpi-delayed" label="Geciken İşler" value={kpi.delayed_count ?? 0} sub="bu hafta geç yapıldı" />
      </div>

      {view === 'live' && <LiveView ov={ov} loading={ovLoading} />}
      {view === 'weekly' && <WeeklyGrid ov={ov} loading={ovLoading} />}
      {view === 'defs' && (
        <div className="space-y-3">
          <Card><CardContent className="p-3 flex items-center gap-2">
            <Label className="text-sm text-slate-600 whitespace-nowrap">Personel:</Label>
            <Select value={selectedPid} onValueChange={setSelectedPid}>
              <SelectTrigger className="w-[240px] h-9" data-testid="routines-personnel-select"><SelectValue placeholder="Personel seçin" /></SelectTrigger>
              <SelectContent>{activePersonnel.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
            </Select>
          </CardContent></Card>
          <RoutineDefinitions personnelId={selectedPid} personnelName={activePersonnel.find(p => p.id === selectedPid)?.name} />
        </div>
      )}
      {view === 'report' && <RoutineReport personnelList={activePersonnel} />}
    </div>
  );
};

/* ============ Bugün Canlı Durum ============ */
const LiveView = ({ ov, loading }) => {
  if (loading) return <Card><CardContent className="py-10 text-center text-slate-400 text-sm">Yükleniyor...</CardContent></Card>;
  const people = (ov?.personnel || []).filter(p => p.today.on_leave || p.today.total > 0);
  if (people.length === 0) return <Card><CardContent className="py-10 text-center text-slate-400 text-sm">Bugün için tanımlı rutin bulunmuyor</CardContent></Card>;
  return (
    <div className="grid md:grid-cols-2 gap-3" data-testid="routines-live">
      {people.map(p => {
        const pct = p.today.total ? Math.round(p.today.done / p.today.total * 100) : 0;
        return (
          <Card key={p.personnel_id} className="border border-slate-200/90" data-testid={`live-person-${p.personnel_id}`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-[#0B172A] flex items-center justify-center text-white text-xs font-bold">{(p.personnel_name || '').split(' ').map(x => x[0]).slice(0, 2).join('')}</div>
                  <span className="font-semibold text-slate-800 text-sm">{p.personnel_name}</span>
                </div>
                {p.today.on_leave ? <Badge className="bg-slate-100 text-slate-500 border-0">İzinli 🌴</Badge>
                  : <span className="text-xs font-bold text-slate-600">{p.today.done}/{p.today.total}</span>}
              </div>
              {!p.today.on_leave && (
                <>
                  <Progress value={pct} className="h-2 mb-3" />
                  <div className="space-y-1.5">
                    {p.today.items.map(it => (
                      <div key={it.routine_id} className="flex items-center gap-2 text-xs">
                        {it.completed
                          ? (it.status === 'gec' ? <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" /> : <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />)
                          : <div className="w-4 h-4 rounded-full border-2 border-slate-300 shrink-0" />}
                        <span className={`flex-1 ${it.completed ? 'line-through text-slate-400' : 'text-slate-700'}`}>{it.title}</span>
                        {it.latest_time && <span className="text-[10px] text-slate-400">≤{it.latest_time}</span>}
                        {it.completed && it.status === 'gec' && <span className="text-[10px] font-semibold text-amber-600">Geç</span>}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

/* ============ Haftalık Izgara ============ */
const cellClass = (c) => {
  if (c.on_leave) return 'bg-slate-100 text-slate-400';
  if (c.total === 0) return 'bg-white text-slate-300';
  if (c.missed > 0) return 'bg-rose-50 text-rose-700';
  if (c.pending > 0) return 'bg-sky-50 text-sky-700';
  if (c.late > 0) return 'bg-amber-50 text-amber-700';
  return 'bg-emerald-50 text-emerald-700';
};
const WeeklyGrid = ({ ov, loading }) => {
  if (loading) return <Card><CardContent className="py-10 text-center text-slate-400 text-sm">Yükleniyor...</CardContent></Card>;
  if (!ov || (ov.personnel || []).length === 0) return <Card><CardContent className="py-10 text-center text-slate-400 text-sm">Tanımlı rutin bulunamadı</CardContent></Card>;
  const days = ov.week_days || [];
  return (
    <Card><CardContent className="p-0 overflow-x-auto">
      <table className="w-full text-xs" data-testid="routines-weekly-grid">
        <thead className="bg-slate-50 border-b">
          <tr className="text-slate-600">
            <th className="px-3 py-2 text-left font-medium sticky left-0 bg-slate-50">Personel</th>
            {days.map((d, i) => <th key={d} className="px-2 py-2 text-center font-medium">{DAYS[i]?.l}<span className="block text-[10px] text-slate-400 font-normal">{dm(d)}</span></th>)}
          </tr>
        </thead>
        <tbody>
          {ov.personnel.map(p => (
            <tr key={p.personnel_id} className="border-b" data-testid={`grid-row-${p.personnel_id}`}>
              <td className="px-3 py-2 font-medium text-slate-800 sticky left-0 bg-white whitespace-nowrap">{p.personnel_name}</td>
              {days.map(d => {
                const c = p.cells[d] || { total: 0 };
                return (
                  <td key={d} className="px-1.5 py-1.5 text-center">
                    <div className={`rounded-md py-1.5 px-1 font-semibold ${cellClass(c)}`} title={c.on_leave ? 'İzinli' : `Zamanında ${c.on_time || 0} / Geç ${c.late || 0} / Yapılmadı ${c.missed || 0} / Bekleyen ${c.pending || 0}`}>
                      {c.on_leave ? 'İzin' : c.total === 0 ? '–' : `${(c.on_time || 0) + (c.late || 0)}/${c.total}`}
                    </div>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="flex flex-wrap gap-3 p-3 border-t text-[11px] text-slate-500">
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-emerald-100 border border-emerald-200" /> Tümü zamanında</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-amber-100 border border-amber-200" /> Geç var</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-sky-100 border border-sky-200" /> Bugün bekleyen</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-rose-100 border border-rose-200" /> Yapılmadı</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-slate-100 border border-slate-200" /> İzinli</span>
      </div>
    </CardContent></Card>
  );
};

/* ============ Rapor ============ */
const RoutineReport = ({ personnelList }) => {
  const [start, setStart] = useState(monthStart());
  const [end, setEnd] = useState(todayISO());
  const [pid, setPid] = useState('all');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [ran, setRan] = useState(false);

  const run = async () => {
    setLoading(true);
    try {
      const params = { start_date: start, end_date: end };
      if (pid !== 'all') params.personnel_id = pid;
      const res = await axios.get(`${API}/routines/report`, { params });
      setRows(res.data?.items || []); setRan(true);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Rapor alınamadı'); }
    finally { setLoading(false); }
  };
  useEffect(() => { run(); /* eslint-disable-next-line */ }, []);
  const rateColor = (r) => r >= 90 ? 'text-emerald-600' : r >= 70 ? 'text-amber-600' : 'text-rose-600';

  return (
    <div className="space-y-4">
      <Card><CardContent className="p-3 flex flex-wrap items-end gap-3">
        <div><Label className="text-xs">Başlangıç</Label><Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="h-9 w-40" data-testid="report-start" /></div>
        <div><Label className="text-xs">Bitiş</Label><Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="h-9 w-40" data-testid="report-end" /></div>
        <div><Label className="text-xs">Personel</Label>
          <Select value={pid} onValueChange={setPid}>
            <SelectTrigger className="w-[190px] h-9" data-testid="report-personnel"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">Tüm Personel</SelectItem>{personnelList.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <Button onClick={run} disabled={loading} className="bg-teal-700 hover:bg-teal-800 h-9" data-testid="report-run-btn"><BarChart3 className="w-4 h-4 mr-1" /> {loading ? 'Analiz...' : 'Analiz Et'}</Button>
      </CardContent></Card>
      <Card><CardContent className="p-0">
        {!ran ? <p className="text-center py-10 text-slate-400 text-sm">Tarih aralığı seçip "Analiz Et"e basın</p>
          : rows.length === 0 ? <p className="text-center py-10 text-slate-400 text-sm flex items-center justify-center gap-2"><CalendarDays className="w-4 h-4" /> Bu aralıkta rutin bulunamadı</p>
          : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="report-table">
                <thead className="bg-slate-50 border-b"><tr className="text-left text-xs text-slate-600">
                  <th className="px-3 py-2 font-medium">Personel</th>
                  <th className="px-2 py-2 font-medium text-center">Beklenen</th>
                  <th className="px-2 py-2 font-medium text-center text-emerald-600">Zamanında</th>
                  <th className="px-2 py-2 font-medium text-center text-amber-600">Geç</th>
                  <th className="px-2 py-2 font-medium text-center text-rose-600">Yapılmadı</th>
                  <th className="px-2 py-2 font-medium text-center text-slate-500">Bekleyen</th>
                  <th className="px-2 py-2 font-medium text-center">Tamamlama</th>
                  <th className="px-2 py-2 font-medium text-center">Zamanında %</th>
                </tr></thead>
                <tbody>
                  {rows.map(r => (
                    <tr key={r.personnel_id} className="border-b hover:bg-slate-50" data-testid={`report-row-${r.personnel_id}`}>
                      <td className="px-3 py-2 font-medium text-slate-800">{r.personnel_name}</td>
                      <td className="px-2 py-2 text-center font-bold">{r.expected}</td>
                      <td className="px-2 py-2 text-center text-emerald-600 font-semibold">{r.on_time}</td>
                      <td className="px-2 py-2 text-center text-amber-600 font-semibold">{r.late}</td>
                      <td className="px-2 py-2 text-center text-rose-600 font-semibold">{r.missed}</td>
                      <td className="px-2 py-2 text-center text-slate-500">{r.pending}</td>
                      <td className={`px-2 py-2 text-center font-bold ${rateColor(r.completion_rate)}`}>%{r.completion_rate}</td>
                      <td className={`px-2 py-2 text-center font-bold ${rateColor(r.on_time_rate)}`}>%{r.on_time_rate}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </CardContent></Card>
      <p className="text-[11px] text-slate-400 px-1">İzinli günler ve gelecek tarihler hesaba katılmaz. "Bekleyen" = bugünkü henüz işaretlenmemiş rutinler.</p>
    </div>
  );
};

export default RoutinesTab;
